import { createEngineResult } from "@energyos/shared-types";
const LEVEL_RANK = { ok: 0, advice: 1, warning: 2, critical: 3 };
function severityToLevel(sev) {
    if (sev === "critical")
        return "critical";
    if (sev === "warning")
        return "warning";
    return "ok";
}
function isNorthish(azimuthDeg) {
    const a = ((azimuthDeg % 360) + 360) % 360;
    return a <= 45 || a >= 315;
}
/** Kantentypen, die als „First" bzw. „Traufe" zählen. */
const FIRST_TYPES = new Set(["first"]);
const TRAUFE_TYPES = new Set(["traufe"]);
/**
 * Deterministische Prüfampel je Dachmodell. Liefert pro Aspekt einen ChecklistItem
 * plus aggregiertes verdict. IMMER requiresHumanReview=true / canApprove=false.
 */
export function evaluateRoofChecklist(input) {
    const findings = [];
    const checks = [];
    const surfaces = input.surfaces ?? [];
    const edgeTypes = input.edgeTypes ?? [];
    const obstacles = input.obstacles ?? [];
    function add(item, findingSeverity) {
        checks.push(item);
        if (findingSeverity) {
            findings.push({ code: item.code, category: "pruefampel", severity: findingSeverity, message: item.message ?? item.title });
        }
    }
    // 1. Geometrie vollständig — mind. eine Fläche mit ≥3 Eckpunkten, Fläche > 0
    //    UND überschneidungsfrei (kein Bowtie). Ein selbstschneidendes Polygon wirkt
    //    plausibel (Eckpunkte/Fläche > 0), liefert aber 0/falsche Module → ehrlich als
    //    unvollständig melden statt grün „ok".
    const validSurfaces = surfaces.filter((s) => s.vertexCount >= 3 && s.areaM2 > 0 && s.simple !== false);
    const selfIntersecting = surfaces.filter((s) => s.simple === false && s.vertexCount >= 3 && s.areaM2 > 0);
    if (surfaces.length === 0) {
        add({ code: "geometry_missing", title: "Geometrie vollständig", state: "critical", message: "Keine Dachfläche vorhanden." }, "critical");
    }
    else if (validSurfaces.length < surfaces.length) {
        const msg = selfIntersecting.length > 0
            ? `Mindestens eine Fläche überschneidet sich oder ist unvollständig (gekreuzte Kanten / < 3 Eckpunkte / 0 m²) — Belegung unzuverlässig, Eckpunkte entwirren.`
            : "Mindestens eine Fläche ist unvollständig (< 3 Eckpunkte oder 0 m²).";
        add({ code: "geometry_incomplete", title: "Geometrie vollständig", state: "warning", message: msg }, "warning");
    }
    else {
        add({ code: "geometry_ok", title: "Geometrie vollständig", state: "ok", message: `${validSurfaces.length} Dachfläche(n) geschlossen.` });
    }
    // 2. First/Traufe gesetzt — semantische Kanten vorhanden.
    const hasFirst = edgeTypes.some((t) => FIRST_TYPES.has(t));
    const hasTraufe = edgeTypes.some((t) => TRAUFE_TYPES.has(t));
    if (hasFirst && hasTraufe) {
        add({ code: "edges_ok", title: "First/Traufe gesetzt", state: "ok" });
    }
    else {
        const missing = [!hasFirst ? "First" : null, !hasTraufe ? "Traufe" : null].filter(Boolean).join(" + ");
        add({ code: "edges_missing", title: "First/Traufe gesetzt", state: "warning", message: `${missing} fehlt — Dachkanten als Linienobjekte erfassen.` }, "warning");
    }
    // 3. Neigung plausibel (5–65°).
    const extremeTilt = validSurfaces.filter((s) => s.tiltDeg < 5 || s.tiltDeg > 65);
    if (validSurfaces.length === 0) {
        add({ code: "tilt_unknown", title: "Neigung plausibel", state: "info", message: "Keine gültige Fläche zum Prüfen." });
    }
    else if (extremeTilt.length > 0) {
        add({ code: "tilt_extreme", title: "Neigung plausibel", state: "info", message: `Ungewöhnliche Neigung bei ${extremeTilt.length} Fläche(n) — prüfen.` }, "info");
    }
    else {
        add({ code: "tilt_ok", title: "Neigung plausibel", state: "ok" });
    }
    // 4. Azimut plausibel (Warnung bei Nordausrichtung).
    const northish = validSurfaces.filter((s) => isNorthish(s.azimuthDeg));
    if (validSurfaces.length === 0) {
        add({ code: "azimuth_unknown", title: "Azimut plausibel", state: "info", message: "Keine gültige Fläche zum Prüfen." });
    }
    else if (northish.length > 0) {
        add({ code: "azimuth_north", title: "Azimut plausibel", state: "info", message: `${northish.length} Fläche(n) nordseitig — Ertrag prüfen.` }, "info");
    }
    else {
        add({ code: "azimuth_ok", title: "Azimut plausibel", state: "ok" });
    }
    // 5. Hindernisse geprüft — alle mit Höhe (sonst Schatten unbrauchbar).
    if (obstacles.length === 0) {
        add({ code: "obstacles_none", title: "Hindernisse geprüft", state: "info", message: "Keine Hindernisse erfasst — falls vorhanden (Kamin/Gaube/SAT …) ergänzen." });
    }
    else {
        const withoutHeight = obstacles.filter((o) => o.heightM == null || o.heightM <= 0);
        if (withoutHeight.length > 0) {
            add({ code: "obstacles_no_height", title: "Hindernisse geprüft", state: "warning", message: `${withoutHeight.length} Hindernis(se) ohne Höhe — Verschattung nicht berechenbar.` }, "warning");
        }
        else {
            add({ code: "obstacles_ok", title: "Hindernisse geprüft", state: "ok", message: `${obstacles.length} Hindernis(se) mit Höhe.` });
        }
    }
    // 6. Sperrflächen geprüft (informativ — Vorhandensein, nicht Pflicht).
    const keepoutCount = input.keepoutCount ?? 0;
    add({
        code: "keepouts_info",
        title: "Sperrflächen geprüft",
        state: "info",
        message: keepoutCount > 0 ? `${keepoutCount} Sperrfläche(n) definiert.` : "Keine Sperrflächen — Randabstände/Wartungsgang prüfen.",
    });
    // 7. Module passen.
    if (input.layout == null) {
        add({ code: "modules_unknown", title: "Module passen", state: "info", message: "Noch keine Belegung gerechnet." });
    }
    else if (input.layout.moduleCount <= 0) {
        add({ code: "modules_none", title: "Module passen", state: "warning", message: "Keine Module platzierbar — Fläche/Abstände prüfen." }, "warning");
    }
    else {
        const dens = input.layout.packingDensityPct;
        add({ code: "modules_ok", title: "Module passen", state: "ok", message: `${input.layout.moduleCount} Module${dens != null ? `, ${Math.round(dens)} % Flächennutzung` : ""}.` });
    }
    // 8. Stringspannung ok.
    if (input.stringCheckOk == null) {
        add({ code: "string_unknown", title: "Stringspannung ok", state: "info", message: "Noch nicht geprüft (Strangauslegung offen)." });
    }
    else if (input.stringCheckOk) {
        add({ code: "string_ok", title: "Stringspannung ok", state: "ok" });
    }
    else {
        add({ code: "string_fail", title: "Stringspannung ok", state: "critical", message: "Strangspannung außerhalb WR-Fenster — Auslegung korrigieren." }, "critical");
    }
    // 9. PVGIS-Ertrag vorhanden.
    const sy = input.specificYieldKwhPerKwp;
    if (sy == null || sy <= 0) {
        add({ code: "yield_missing", title: "PVGIS-Ertrag vorhanden", state: "warning", message: "Kein Ertragswert — PVGIS/Simulation ausführen." }, "warning");
    }
    else {
        add({ code: "yield_ok", title: "PVGIS-Ertrag vorhanden", state: "ok", message: `${Math.round(sy)} kWh/kWp.` });
    }
    // 10. Menschliche Prüfung erforderlich — strukturell, immer offen.
    add({ code: "human_review", title: "Menschliche Prüfung erforderlich", state: "info", message: "ENTWURF — Freigabe nur durch Mensch." });
    const verdict = findings.reduce((acc, f) => {
        const lvl = severityToLevel(f.severity);
        return LEVEL_RANK[lvl] > LEVEL_RANK[acc] ? lvl : acc;
    }, "ok");
    const okCount = checks.filter((c) => c.state === "ok").length;
    const summary = `Dach-Prüfampel: ${okCount}/${checks.length} Punkte erfüllt (${verdict}). Menschliche Prüfung erforderlich.`;
    const data = {
        verdict,
        summary,
        checks,
        metrics: {
            surfaces: surfaces.length,
            validSurfaces: validSurfaces.length,
            obstacles: obstacles.length,
            okChecks: okCount,
            totalChecks: checks.length,
        },
        requiresHumanReview: true,
        canApprove: false,
    };
    const status = verdict === "critical" ? "needs_user_review" : findings.length > 0 ? "success_with_warnings" : "success";
    const result = createEngineResult(status, data, { confidenceScore: verdict === "ok" ? 0.85 : 0.6 });
    result.findings = findings;
    return result;
}
//# sourceMappingURL=roof-checklist.js.map